import { useRef, useState } from 'react';
import { Columns3, GripVertical, RotateCcw } from 'lucide-react';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Button, buttonVariants } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { useTranslation } from '@/hooks/useTranslation';
import { cn } from '@/lib/utils';
import type { ColumnConfig } from './types';
import { isColumnHideable } from './useColumnPrefs';

interface ColumnManagerPanelProps {
  /** `manageableColumns` from `useColumnPrefs` — current order, hidden included. */
  columns: ColumnConfig[];
  hiddenSet: ReadonlySet<string>;
  onToggleHidden: (colData: string) => void;
  /** Move a column to a final index within `columns`. */
  onMove: (colData: string, toIndex: number) => void;
  onReset: () => void;
  /** Toolbar placement — renders the trigger at the 32px ghost-icon size
   *  to match the buttons beside it (fullscreen). Default: the compact
   *  action-column-header style. */
  toolbar?: boolean;
}

interface DragState {
  colData: string;
  pointerId: number;
  fromIndex: number;
  cleanup: () => void;
}

/**
 * The Columns popover for `EditableTable`'s opt-in column management:
 * drag handles reorder (pointer drag within the list, or ArrowUp/Down on a
 * focused handle), checkboxes show/hide, Reset restores defaults. Mirrors
 * `ColumnFilter`'s popover structure and event-isolation rules (clicks and
 * keydowns must not reach the table's cell navigation).
 *
 * Reorders commit on drop — during the drag only an insertion indicator
 * moves, so the table behind the popover re-renders once, not per frame.
 * Sticky columns sit in a leading block: a sticky row can only land among
 * sticky rows and vice versa (the hook clamps again defensively).
 */
export function ColumnManagerPanel({
  columns,
  hiddenSet,
  onToggleHidden,
  onMove,
  onReset,
  toolbar = false,
}: ColumnManagerPanelProps) {
  const { t } = useTranslation('spreadsheet');
  const [open, setOpen] = useState(false);
  const [dragKey, setDragKey] = useState<string | null>(null);
  const [overIndex, setOverIndex] = useState<number | null>(null);
  const listRef = useRef<HTMLDivElement>(null);
  const dragRef = useRef<DragState | null>(null);

  const stickyCount = columns.filter((c) => c.sticky).length;
  // hiddenSet is pre-reconciled by useColumnPrefs to existing hideable
  // columns, so plain size arithmetic is safe.
  const visibleCount = columns.length - hiddenSet.size;

  const clampToPartition = (col: ColumnConfig, index: number) => {
    const [lo, hi] = col.sticky
      ? [0, stickyCount - 1]
      : [stickyCount, columns.length - 1];
    return Math.min(hi, Math.max(lo, index));
  };

  const handleDragStart = (col: ColumnConfig, e: React.PointerEvent) => {
    if (e.button !== 0 || dragRef.current) return;
    const list = listRef.current;
    if (!list) return;
    const fromIndex = columns.findIndex((c) => c.data === col.data);
    if (fromIndex === -1) return;

    // Row midpoints are read live on every evaluation, NOT snapshotted at
    // drag start: the list is scrollable and a wheel-scroll mid-drag
    // (pointer capture doesn't block wheel events) would invalidate any
    // captured positions. ~15 getBoundingClientRect reads per move are
    // cheap — nothing mutates layout during the drag, so reads stay cached.
    const readMids = () =>
      columns.map((c) => {
        const esc = CSS.escape(c.data);
        const row = list.querySelector<HTMLElement>(`[data-col-key="${esc}"]`);
        const rect = row?.getBoundingClientRect();
        // Missing row (shouldn't happen) → +∞ so it never counts as passed.
        return rect ? rect.top + rect.height / 2 : Number.POSITIVE_INFINITY;
      });

    e.preventDefault();
    const captured = e.currentTarget as Element;
    captured.setPointerCapture?.(e.pointerId);

    const state: DragState = {
      colData: col.data,
      pointerId: e.pointerId,
      fromIndex,
      cleanup: () => {},
    };

    const targetFor = (clientY: number) => {
      const mids = readMids();
      // Final index = number of row midpoints above the pointer (drop on
      // the row under the cursor and take its place). Once the pointer is
      // below the dragged row's own midpoint, the dragged row has vacated
      // its slot, so the count over-shoots by one.
      let index = mids.filter((mid) => clientY > mid).length;
      if (clientY > mids[state.fromIndex]) index = Math.max(0, index - 1);
      return clampToPartition(col, index);
    };

    const onMovePointer = (ev: PointerEvent) => {
      if (ev.pointerId !== state.pointerId) return;
      setOverIndex(targetFor(ev.clientY));
    };
    const onUp = (ev: PointerEvent) => {
      if (ev.pointerId !== state.pointerId) return;
      const target = targetFor(ev.clientY);
      state.cleanup();
      if (target !== state.fromIndex) onMove(state.colData, target);
    };
    const onCancel = (ev: PointerEvent) => {
      if (ev.pointerId !== state.pointerId) return;
      state.cleanup();
    };

    state.cleanup = () => {
      window.removeEventListener('pointermove', onMovePointer);
      window.removeEventListener('pointerup', onUp);
      window.removeEventListener('pointercancel', onCancel);
      try {
        captured.releasePointerCapture?.(state.pointerId);
      } catch {
        // pointer already released – ignore
      }
      dragRef.current = null;
      setDragKey(null);
      setOverIndex(null);
    };

    dragRef.current = state;
    setDragKey(col.data);
    setOverIndex(fromIndex);
    window.addEventListener('pointermove', onMovePointer);
    window.addEventListener('pointerup', onUp);
    window.addEventListener('pointercancel', onCancel);
  };

  const handleHandleKeyDown = (col: ColumnConfig, index: number, e: React.KeyboardEvent) => {
    if (e.key !== 'ArrowUp' && e.key !== 'ArrowDown') return;
    e.preventDefault();
    e.stopPropagation();
    const target = clampToPartition(col, e.key === 'ArrowUp' ? index - 1 : index + 1);
    if (target !== index) onMove(col.data, target);
  };

  const lockedTitle = (col: ColumnConfig) =>
    col.required ? t('columns.requiredLocked') : t('columns.pinnedLocked');

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          aria-label={t('columns.trigger')}
          title={t('columns.trigger')}
          className={
            toolbar
              ? // Toolbar placement — the real ghost icon-button classes
                // (focus-visible ring, disabled styles included) so it
                // matches the fullscreen Button beside it in Toolbar.tsx.
                cn(buttonVariants({ variant: 'ghost', size: 'icon' }), 'h-8 w-8')
              : cn(
                  'inline-flex items-center justify-center transition-colors',
                  'rounded-sm p-1 text-muted-foreground hover:text-foreground',
                )
          }
          onClick={(e) => e.stopPropagation()}
        >
          <Columns3 className={toolbar ? 'h-4 w-4' : 'h-3.5 w-3.5'} />
        </button>
      </PopoverTrigger>
      <PopoverContent
        className="w-64 p-0"
        align="start"
        onKeyDown={(e) => e.stopPropagation()}
      >
        <div className="px-3 py-2 border-b text-sm font-medium">
          {t('columns.title')}
        </div>
        {/* Native scroll container — NOT Radix ScrollArea: its viewport is
            `h-full`, which doesn't resolve against a max-height-only root,
            so content past the cap clips without ever scrolling. */}
        <div className="max-h-80 overflow-y-auto">
          <div ref={listRef} className="p-1">
            {columns.map((col, index) => {
              const hideable = isColumnHideable(col);
              const isDragged = dragKey === col.data;
              const showAbove =
                dragKey !== null && overIndex === index && !isDragged &&
                overIndex < (dragRef.current?.fromIndex ?? -1);
              const showBelow =
                dragKey !== null && overIndex === index && !isDragged &&
                overIndex > (dragRef.current?.fromIndex ?? -1);
              return (
                <div
                  key={col.data}
                  data-col-key={col.data}
                  className={cn(
                    'flex items-center gap-2 px-2 py-1.5 rounded-sm hover:bg-accent',
                    isDragged && 'opacity-50',
                    showAbove && 'shadow-[inset_0_2px_0_0_hsl(var(--primary))]',
                    showBelow && 'shadow-[inset_0_-2px_0_0_hsl(var(--primary))]',
                  )}
                >
                  <button
                    type="button"
                    aria-label={t('columns.dragHandle', { column: col.header })}
                    className="cursor-grab touch-none text-muted-foreground hover:text-foreground focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring rounded-sm"
                    onPointerDown={(e) => handleDragStart(col, e)}
                    onKeyDown={(e) => handleHandleKeyDown(col, index, e)}
                  >
                    <GripVertical className="h-3.5 w-3.5" />
                  </button>
                  <Checkbox
                    checked={!hiddenSet.has(col.data)}
                    disabled={!hideable}
                    onCheckedChange={() => onToggleHidden(col.data)}
                    aria-label={t('columns.showColumn', { column: col.header })}
                    title={hideable ? undefined : lockedTitle(col)}
                  />
                  <span
                    className={cn(
                      'text-sm truncate flex-1 select-none',
                      hiddenSet.has(col.data) && 'text-muted-foreground',
                    )}
                  >
                    {col.header}
                  </span>
                </div>
              );
            })}
          </div>
        </div>
        <div className="flex items-center justify-between border-t p-2">
          <span className="text-xs text-muted-foreground">
            {t('columns.visibleSummary', { visible: visibleCount, total: columns.length })}
          </span>
          <Button
            variant="ghost"
            size="sm"
            className="h-7 px-2 text-xs"
            onClick={onReset}
          >
            <RotateCcw className="h-3 w-3 mr-1" />
            {t('columns.reset')}
          </Button>
        </div>
      </PopoverContent>
    </Popover>
  );
}
